import { siteApi } from "@api";
import {
  calculateFinalClaimableAmount,
  shouldShowFromApiData,
  shouldShowFromWSData,
} from "@components/features/SkinnerBox/SkinnerBoxFAB/utils/helpers";
import { WSSkinnerBoxAmountChangedEvent } from "@constants";
import useUserMode from "@hooks/useUserMode";
import { AppDispatch, RootState } from "@stores";
import { SkinnerBoxSliceState } from "@stores/skinnerBox";
import {
  DEFAULT_SKINNER_BOX_KEY,
  resetSkinnerBoxData,
  updateSkinnerBoxFromAPI,
  updateSkinnerBoxFromWS,
} from "@stores/skinnerBox/slices";
import logger from "@utils/logger";
import { GetRewardsStatusResponse, WalletTxMethod } from "kz-ui-sdk";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useWSSkinnerBoxAmountChanged } from "./useAppWS";

const useSkinnerBoxStatus = () => {
  const { isGuest } = useUserMode();
  const dispatch = useDispatch<AppDispatch>();
  const isMountedRef = useRef(false);
  const [rewardsStatusData, setRewardsStatusData] = useState<GetRewardsStatusResponse | null>(null);

  // Get SkinnerBox state from Redux store
  const { skinnerBoxKey, showSkinnerBoxFromWS, wsClaimableAmount, shouldShowSkinnerBox, skinnerBoxClaimableAmount } =
    useSelector<RootState, SkinnerBoxSliceState>((state) => state.skinnerBox);

  const fetchAndSetRewardsStatus = useCallback(async () => {
    try {
      if (isGuest) return;
      const result = await dispatch(
        siteApi.endpoints.getRewardsStatus.initiate(undefined, {
          forceRefetch: true,
        }),
      );
      if ((result as any)?.data) {
        setRewardsStatusData((result as any).data);
      }
    } catch (error) {
      logger._console.error("Failed to fetch rewards status", error);
    }
  }, [dispatch, isGuest]);

  // Track mounted state and fetch rewards status on mount
  useEffect(() => {
    isMountedRef.current = true;
    fetchAndSetRewardsStatus();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchAndSetRewardsStatus]);

  // Extract skinner box data from rewards status
  const skinnerBoxData = useMemo(() => {
    return rewardsStatusData?.[WalletTxMethod.SkinnerBox];
  }, [rewardsStatusData]);

  // Calculate final shouldShowSkinnerBox based on both API and WS data
  const finalShouldShowSkinnerBox = useMemo(() => {
    // Show SkinnerBox from WebSocket event
    if (shouldShowFromWSData(showSkinnerBoxFromWS, wsClaimableAmount)) {
      return true;
    }

    // Show SkinnerBox from API data
    return shouldShowFromApiData(skinnerBoxData);
  }, [skinnerBoxData, showSkinnerBoxFromWS, wsClaimableAmount]);

  // Calculate final claimable amount
  const finalClaimableAmount = useMemo(() => {
    return calculateFinalClaimableAmount(wsClaimableAmount, skinnerBoxData?.claimableAmount);
  }, [skinnerBoxData, wsClaimableAmount]);

  // Update Redux state when API data changes
  useEffect(() => {
    dispatch(
      updateSkinnerBoxFromAPI({
        shouldShow: finalShouldShowSkinnerBox,
        claimableAmount: finalClaimableAmount,
      }),
    );
  }, [finalShouldShowSkinnerBox, finalClaimableAmount, dispatch]);

  // Handle skinner box amount changed event from WebSocket
  const handleSkinnerBoxAmountChanged = useCallback(
    (event?: WSSkinnerBoxAmountChangedEvent) => {
      if (event?.detail?.availableAmount && event.detail.availableAmount > 0) {
        dispatch(
          updateSkinnerBoxFromWS({
            amount: event.detail.availableAmount,
            shouldShow: true,
          }),
        );
      }
    },
    [dispatch],
  );

  // Subscribe to WebSocket events
  useWSSkinnerBoxAmountChanged({ cb: handleSkinnerBoxAmountChanged });

  // Handle animation completion: reset state after animation finishes
  const handleLeaveAnimationCompleted = useCallback(() => {
    // Reset all SkinnerBox state after animation completes
    dispatch(resetSkinnerBoxData());
    if (isMountedRef.current) {
      fetchAndSetRewardsStatus();
    }
  }, [dispatch, fetchAndSetRewardsStatus]);

  if (isGuest)
    return {
      shouldShowSkinnerBox: false,
      skinnerBoxClaimableAmount: 0,
      skinnerBoxKey: DEFAULT_SKINNER_BOX_KEY,
      handleLeaveAnimationCompleted: () => {},
    };

  return {
    shouldShowSkinnerBox,
    skinnerBoxClaimableAmount,
    skinnerBoxKey,
    handleLeaveAnimationCompleted,
  };
};

export default useSkinnerBoxStatus;
